import React, { Component } from 'react';
import DynamicFormRenderer from '../renderer/DynamicFormRenderer';
import { resolveFormDefinition } from '../../../services/clinicPortalServices/formDefinitionService';
import { apiInterfaceForJson } from '../../../services/common/apiManager';
import { getUserFullName } from '../../../services/common/util';
import toastr from 'toastr';

/**
 * DynamicFormModal - Modal wrapper that:
 * 1. Resolves a form definition for an entity type
 * 2. Optionally loads entity data for editing
 * 3. Submits to existing entity APIs on save
 *
 * Props:
 * - entityType: string (e.g. 'company', 'facility', 'instrument')
 * - entityId: string (ID for editing, null for creating)
 * - context: AG-Grid context for refreshing grid
 * - handleClose: function to close modal
 * - companyId: optional company ID for per-company form resolution
 * - entityConfig: { getByIdEndpoint, createEndpoint, updateEndpoint, getByIdMethod }
 * - slug: optional slug override
 */
export default class DynamicFormModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            formDefinition: null,
            initialData: null,
            loading: true,
            error: null
        };
        this.formRef = React.createRef();
    }

    componentDidMount() {
        this.loadFormDefinition();
    }

    loadFormDefinition = () => {
        const { entityType, slug, companyId, entityId, entityConfig } = this.props;

        resolveFormDefinition({
            entityType: entityType,
            slug: slug || entityType,
            companyId: companyId || undefined
        })
            .then((response) => {
                if (response.status === 200 && response.data) {
                    this.setState({ formDefinition: response.data }, () => {
                        if (entityId && entityConfig && entityConfig.getByIdEndpoint) {
                            this.loadEntityData();
                        } else {
                            this.setState({ loading: false });
                        }
                    });
                } else {
                    this.setState({ error: 'Form definition not found', loading: false });
                }
            })
            .catch((err) => {
                this.setState({ error: 'Failed to load form definition', loading: false });
            });
    };

    loadEntityData = () => {
        const { entityId, entityConfig } = this.props;
        const endpoint = entityConfig.getByIdEndpoint.replace(':id', entityId);
        const method = entityConfig.getByIdMethod || 'GET';

        apiInterfaceForJson(endpoint, method)
            .then((response) => {
                let data = response.data;
                // Handle cases where data is an array (e.g., company returns array)
                if (Array.isArray(data)) {
                    data = data[0];
                }
                this.setState({ initialData: data, loading: false });
            })
            .catch((err) => {
                this.setState({ error: 'Failed to load entity data', loading: false });
            });
    };

    handleSubmit = (modelData) => {
        const { entityId, entityConfig, handleClose, context } = this.props;

        if (entityId) {
            // Update
            modelData._id = entityId;
            const endpoint = entityConfig.updateEndpoint || entityConfig.createEndpoint;
            const method = entityConfig.updateMethod || 'PUT';

            apiInterfaceForJson(endpoint, method, modelData)
                .then((response) => {
                    if (context && context.componentParent && context.componentParent.loadGridData) {
                        context.componentParent.loadGridData();
                    }
                    handleClose();
                    toastr.success(this.props.entityType + ' updated successfully');
                })
                .catch((err) => {
                    toastr.error('Failed to update');
                });
        } else {
            // Create
            modelData.createdBy = getUserFullName();
            const endpoint = entityConfig.createEndpoint;
            const method = entityConfig.createMethod || 'POST';

            apiInterfaceForJson(endpoint, method, modelData)
                .then((response) => {
                    if (context && context.componentParent && context.componentParent.loadGridData) {
                        context.componentParent.loadGridData();
                    }
                    handleClose();
                    toastr.success(this.props.entityType + ' created successfully');
                })
                .catch((err) => {
                    toastr.error('Failed to create');
                });
        }
    };

    render() {
        const { handleClose } = this.props;
        const { formDefinition, initialData, loading, error } = this.state;

        return (
            <div>
                {loading && (
                    <div className="text-center p-4">
                        <i className="fa fa-spinner fa-spin fa-2x" />
                        <p className="mt-2">Loading form...</p>
                    </div>
                )}
                {error && (
                    <div className="text-center p-4">
                        <p className="text-danger">{error}</p>
                        <button className="btn btn-secondary" onClick={handleClose}>
                            Close
                        </button>
                    </div>
                )}
                {!loading && !error && formDefinition && (
                    <DynamicFormRenderer
                        ref={this.formRef}
                        formDefinition={formDefinition}
                        initialData={initialData}
                        onSubmit={this.handleSubmit}
                        onClose={handleClose}
                    />
                )}
            </div>
        );
    }
}

/**
 * Entity config presets for quick setup.
 * Usage: <DynamicFormModal entityConfig={ENTITY_CONFIGS.instrument} ... />
 */
export const ENTITY_CONFIGS = {
    instrument: {
        getByIdEndpoint: '/instrument/v1/:id',
        createEndpoint: '/instrument/v1/',
        updateEndpoint: '/instrument/v1/',
        updateMethod: 'PUT'
    },
    lab: {
        getByIdEndpoint: '/lab/v1/:id',
        createEndpoint: '/lab/v1/',
        updateEndpoint: '/lab/v1/',
        updateMethod: 'PUT'
    },
    company: {
        getByIdEndpoint: '/company/v1/:id',
        createEndpoint: '/company/v1/',
        updateEndpoint: '/company/v1/',
        updateMethod: 'PUT'
    },
    facility: {
        getByIdEndpoint: '/facility/v1/:id',
        createEndpoint: '/facility/v1/',
        updateEndpoint: '/facility/v1/',
        updateMethod: 'PUT'
    },
    physician: {
        getByIdEndpoint: '/physician/v1/:id',
        createEndpoint: '/physician/v1/',
        updateEndpoint: '/physician/v1/',
        updateMethod: 'PUT'
    }
};
